import { Trans, useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import { callAPI } from "../../../lib/common";
import Box from "@mui/material/Box";
import { TextField } from "@mui/material";
import LoadingButton from "../../../components/LoadingButton";
import BoxContainer from "./BoxContainer";
import BoxHeading from "./BoxHeading";
import makeStyles from "@mui/styles/makeStyles";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../providers/AuthProvider";
import WarningLastSubjectNotSelectedDialog from "../WarningLastSubjectNotSelectedDialog";
const useStyles = makeStyles((theme) => ({
    button: {
        minWidth: "136px",
        paddingRight: 0,
        paddingLeft: 0,
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: {
            minWidth: "96px",
        },
    },
    buttonContainer: {
        marginLeft: theme.spacing(2),
    },
    form: {
        marginBottom: 0,
    },
    formContainer: {
        [theme.breakpoints.down("sm")]: {
            "& > *": {
                flexBasis: "50%",
                flexGrow: 1,
                flexShrink: 1,
            },
        },
    },
    notReceivedCodeLink: {
        color: theme.palette.primary.main,
    },
}));
const EnterCode = ({ handleSetCodeData, handleLoginSuccess, codeData, setDisplayNotReceivedCodeDialog, code, setCode, autoSubmitCode, }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [isVerifying, setIsVerifying] = useState(false);
    const { isAuthenticated, user } = useAuth();
    const [warning, setWarning] = useState(null);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const handleSubmit = useCallback(({ event, accessCode, }) => {
        if (event) {
            event.preventDefault();
        }
        if (user === null || user === void 0 ? void 0 : user.isSimulated) {
            if (!confirm(t("Are you sure you want to add the code %1$s to user %2$s?", {
                postProcess: "sprintf",
                sprintf: [accessCode, user.name],
            }))) {
                return;
            }
        }
        setIsVerifying(true);
        setError(null);
        if (isAuthenticated) {
            callAPI("/ajax/prophoto/add_picture_set_by_code.php?code=" + accessCode)
                .then((result) => {
                var _a;
                if (result.error || !result.valid) {
                    setError((_a = result.error) !== null && _a !== void 0 ? _a : null);
                    setIsVerifying(false);
                }
                else if (result.warning) {
                    setIsVerifying(false);
                    setWarning(result.warning);
                }
                else if (result.redirect) {
                    document.location.href = result.redirect;
                }
                else {
                    handleLoginSuccess();
                }
            })
                .catch(() => {
                setIsVerifying(false);
            });
        }
        else {
            callAPI("/ajax/prophoto/verify_subject_code.php?code=" + accessCode)
                .then((result) => {
                if (!result.valid) {
                    setError("errorMessage" in result ? result.errorMessage : result.error);
                    setIsVerifying(false);
                }
                else if (result.data.redirect) {
                    document.location.href = result.data.redirect;
                }
                else {
                    handleSetCodeData({
                        name: result.data.name,
                        accessCode,
                    });
                    setIsVerifying(false);
                }
            })
                .catch(() => {
                setIsVerifying(false);
            });
        }
    }, [
        handleLoginSuccess,
        handleSetCodeData,
        isAuthenticated,
        t,
        user === null || user === void 0 ? void 0 : user.isSimulated,
        user === null || user === void 0 ? void 0 : user.name,
    ]);
    // If we have a GET-code on mount, auto-submit it once:
    useEffect(() => {
        if (autoSubmitCode) {
            handleSubmit({ accessCode: autoSubmitCode });
        }
        // intentional: run once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (codeData === null || codeData === void 0 ? void 0 : codeData.accessCode) {
            setCode(codeData.accessCode);
        }
        if (codeData === null || codeData === void 0 ? void 0 : codeData.errorMessage) {
            setError(codeData.errorMessage);
        }
    }, [codeData, setCode]);
    const handleCodeChange = (e) => {
        setCode(e.target.value);
    };
    if (warning) {
        return (React.createElement(WarningLastSubjectNotSelectedDialog, { subject: warning.payload.subject, handleOk: handleLoginSuccess }));
    }
    return (React.createElement(BoxContainer, { "data-testid": "enter-code-box" },
        React.createElement(BoxHeading, null, isAuthenticated ? (React.createElement(Trans, null, "Would you like to add another picture set?")) : (React.createElement(Trans, null, "Hi! Did you receive a code from us?"))),
        (user === null || user === void 0 ? void 0 : user.isSimulated) && (React.createElement(Box, { mb: 2, color: "red" }, t("Simulating user %s / %s", {
            postProcess: "sprintf",
            sprintf: [user.name, user.emailAddress],
        }))),
        React.createElement("form", { onSubmit: (e) => handleSubmit({ event: e, accessCode: code }), className: classes.form },
            React.createElement(Box, { display: "flex", justifyContent: "space-between", className: classes.formContainer },
                React.createElement(TextField, { id: "front-page-enter-code", label: t("Code"), variant: "outlined", onChange: handleCodeChange, helperText: error, error: !!error, value: code, size: "medium", color: "secondary", style: (user === null || user === void 0 ? void 0 : user.isSimulated) ? { backgroundColor: "#ff00004f" } : {}, autoFocus: true }),
                React.createElement(Box, { className: classes.buttonContainer },
                    React.createElement(LoadingButton, { isLoading: isVerifying, variant: "primary", size: "large", className: classes.button, fullWidth: true, style: (user === null || user === void 0 ? void 0 : user.isSimulated)
                            ? { backgroundColor: "red", color: "white" }
                            : {}, "data-testid": "login-with-code-button" }, isAuthenticated ? (React.createElement(Trans, null, "Add pictures")) : (React.createElement(Trans, null, "Continue"))))),
            React.createElement(Box, { mt: 2, display: "flex", justifyContent: "center", position: "relative", top: theme.breakpoints.down("sm") ? theme.spacing(1) : theme.spacing(2) },
                React.createElement("a", { href: "#", className: classes.notReceivedCodeLink, onClick: () => setDisplayNotReceivedCodeDialog(true) },
                    React.createElement(Trans, null, "Not received a code?"))))));
};
export default EnterCode;
